import {
  type ClientContext,
  type ClientEvent,
  type ClientEventPayload,
  ClientTrackEventRequest,
} from "@quotientjs/core"
import type { BrowserApiClient } from "@/browser-api-client"
import type { BrowserContextManager } from "@/clients/browser-context"

// Share this state across all analytics clients on the page so browser history
// is only patched once. A new client replaces the active handler instead of
// adding another wrapper, which prevents duplicate tracking after navigation.
// lastTrackedPageKey lives here too, not on the instance, so dedup still works
// if the SDK ends up mounted twice or the client gets re-initialized.
let historyPatched = false
let activeNavigationHandler: (() => void) | null = null
let lastTrackedPageKey: string | undefined

function ensureHistoryPatched(): void {
  if (historyPatched) {
    return
  }
  historyPatched = true

  const dispatch = (): void => {
    activeNavigationHandler?.()
  }

  window.addEventListener("popstate", dispatch)

  const pushState = window.history.pushState.bind(window.history)
  window.history.pushState = (...args: Parameters<History["pushState"]>) => {
    pushState(...args)
    dispatch()
  }

  const replaceState = window.history.replaceState.bind(window.history)
  window.history.replaceState = (
    ...args: Parameters<History["replaceState"]>
  ) => {
    replaceState(...args)
    dispatch()
  }
}

export class Analytics {
  private apiClient: BrowserApiClient
  private browserContext: BrowserContextManager
  private pageViewTrackingInitialized = false
  private navigationHandler: (() => void) | null = null

  constructor(
    apiClient: BrowserApiClient,
    browserContext: BrowserContextManager,
  ) {
    this.apiClient = apiClient
    this.browserContext = browserContext
  }

  /**
   * Sends an analytics event with the current session, attribution, and
   * browser details.
   *
   * Reuse `idempotencyKey` when retrying an event. Quotient checks for earlier
   * submissions for 24 hours. A Quotient outage can still let a duplicate
   * through; concurrent retries cannot.
   */
  async event(
    event: ClientEvent & Pick<ClientContext, "idempotencyKey">,
  ): Promise<void> {
    await this.trackCurrentPage(event, { deduplicate: false })
  }

  /**
   * Starts automatic page-view tracking. It records the current page, then
   * tracks future browser navigation. `QuotientClient.init()` calls this once.
   */
  initPageViewTracking(): void {
    if (this.pageViewTrackingInitialized) {
      return
    }
    this.pageViewTrackingInitialized = true

    ensureHistoryPatched()
    this.navigationHandler = (): void => {
      void this.trackCurrentPage(
        { eventType: "pageView" },
        { deduplicate: true },
      )
    }
    activeNavigationHandler = this.navigationHandler

    void this.trackCurrentPage({ eventType: "pageView" }, { deduplicate: true })
  }

  /**
   * Stops this client from tracking browser navigation. Call this when the
   * client is no longer in use, such as when its React provider unmounts.
   */
  disablePageViewTracking(): void {
    if (activeNavigationHandler === this.navigationHandler) {
      activeNavigationHandler = null
    }
    this.navigationHandler = null
    this.pageViewTrackingInitialized = false
  }

  /**
   * Sends an event for the current page. Automatic page views are sent only
   * once per URL, while events sent through `event()` are always recorded.
   */
  private async trackCurrentPage(
    event: ClientEvent & Pick<ClientContext, "idempotencyKey">,
    { deduplicate }: { deduplicate: boolean },
  ): Promise<void> {
    const pageKey = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (deduplicate && pageKey === lastTrackedPageKey) {
      return
    }
    lastTrackedPageKey = pageKey

    const context = this.browserContext.prepare()
    this.apiClient.setAnalyticsSessionId(context.sessionId)
    this.apiClient.setReferrerUrl(context.referrerUrl ?? null)

    const payload: ClientEventPayload = {
      ...context.attribution,
      ...(context.personId ? { personId: context.personId } : {}),
      ...event,
      pathname: window.location.pathname,
      pageUrl: window.location.href,
    }

    await this.apiClient.call(new ClientTrackEventRequest(payload))
  }
}
